import React from "react";
import OrganisationLinkCard from "./component/OrganisationLinkCard";
import { Cable, CircleCheckBig, RefreshCw, Users, X } from "lucide-react";
import WorkspaceSection from "./component/WorkspaceSection";
import WhatsAppAccountSection from "./component/WhatsAppAccountSection";
import PhoneNumberSection from "./component/PhoneNumberSection";
import ActiveConnection from "./component/ActiveConnection";
import SaveConnectionBar from "./component/SaveConnectionBar";
import WorkspaceModal from "./modal/WorkspaceModal";
import PhoneNumberModal from "./modal/PhoneNumberModal";
import useWhatsAppConnection from "./hooks/useWhatsAppConnection";
import WhatsAppAccountModal from "./modal/WhatsAppAccountModal";

function WhatsAppConnectionSettings({ mode = "settings", onSetupComplete }) {
  const connection = useWhatsAppConnection({
    mode,
    onSetupComplete,
  });

  const {
    isSetupMode,
    canManageConnection,
    canSetDefaultConnection,
    isDefaultConnection,
    useAsDefault,
    setUseAsDefault,
    hasConnection,
    connectionStatus,
    reload,
    workspaces,
    accounts,
    phoneNumbers,
    workspaceId,
    whatsappAccountId,
    phoneNumberId,
    loading,
    loadingAccounts,
    loadingPhones,
    saving,
    creating,
    error,
    saved,
    modal,
    workspaceForm,
    accountForm,
    phoneForm,
    accountModalMode,
    workspaceName,
    accountName,
    phoneName,
    phoneNumber,
    setError,
    setModal,
    setWorkspaceForm,
    setAccountForm,
    setPhoneForm,
    handleWorkspaceChange,
    handleAccountChange,
    handlePhoneNumberChange,
    handleCreateWorkspace,
    handleCreateAccount,
    handleUpdateAccount,
    handleCreatePhoneNumber,
    handleSave,
    openWorkspaceModal,
    openAccountModal,
    openEditAccountModal,
    openPhoneModal,
  } = connection;

  const memberWaiting =
    !loading &&
    !loadingAccounts &&
    !loadingPhones &&
    !canManageConnection &&
    (isSetupMode || !hasConnection);

  return (
    <div className="min-w-0 text-[#102a3d] dark:text-dark-text-primary">
      <div className="space-y-4 sm:space-y-5">
        <section className="relative overflow-hidden rounded-[22px] border border-[#d8e7ed] bg-white p-4 shadow-[0_14px_40px_rgba(15,43,66,0.06)] sm:p-5 dark:border-dark-border dark:bg-dark-card">
          <div className="pointer-events-none absolute -right-14 -top-20 h-44 w-44 rounded-full bg-[#e6f5fb] dark:bg-dark-soft-blue" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] border border-[#b9dff0] bg-[#edf8fd] text-[#147db5] dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary">
                <Cable size={17} strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#147db5] dark:text-dark-primary">
                  Primary connection
                </p>
                <h2 className="mt-1 text-[17px] font-bold tracking-[-0.025em] text-[#173044] dark:text-dark-text-primary">
                  {isSetupMode
                    ? "Open your organisation’s WhatsApp"
                    : "Choose what powers this workspace"}
                </h2>
                <p className="mt-1.5 max-w-[660px] text-[10px] font-medium leading-5 text-[#607787] dark:text-dark-text-secondary">
                  Use your organisation’s shared WhatsApp connection for
                  conversations, automation and reporting.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 rounded-xl border border-[#cfe4ed] bg-white/85 px-3 py-2 text-[9px] font-bold text-[#426479] dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary">
              <CircleCheckBig size={13} />
              Workspace scoped
            </div>
          </div>
        </section>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2.5 rounded-2xl border border-[#efcecc] bg-[#fff7f6] px-3.5 py-3 sm:items-center dark:border-[#603d3b] dark:bg-[#2d211f]">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-danger/10 text-danger">
              <X size={14} />
            </div>

            <p className="min-w-0 flex-1 text-[10px] font-medium leading-4 text-danger sm:text-xs">
              {error}
            </p>

            <button
              type="button"
              onClick={() => setError("")}
              className="shrink-0 text-danger/60 transition-colors hover:text-danger"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <OrganisationLinkCard workspaces={workspaces} onLinked={reload} />
        {!loading && !canManageConnection && !memberWaiting && (
          <p className="rounded-xl border border-border-light bg-card-bg p-4 text-sm text-text-secondary dark:border-dark-border dark:bg-dark-card dark:text-dark-text-secondary">
            {accounts.length
              ? "Choose an existing account and number to open your inbox. Your organisation’s owner or admin manages the connection."
              : "Your organisation’s owner or admin needs to connect WhatsApp, or link the existing WhatsApp workspace, once for the whole team. You don’t need a separate account or credentials."}
          </p>
        )}
        {/* Workspace */}
        <div className="w-full min-w-0">
          <WorkspaceSection
            workspaces={workspaces}
            workspaceId={workspaceId}
            loading={loading}
            onSelect={handleWorkspaceChange}
            onAdd={
              workspaces.some((workspace) => workspace.can_manage_connection) ||
              !workspaces.length
                ? openWorkspaceModal
                : undefined
            }
          />
        </div>

        {memberWaiting && (
          <section
            className="rounded-[22px] border border-[#d8e7ed] bg-white p-6 sm:p-8 dark:border-dark-border dark:bg-dark-card"
            aria-label="Organisation connection status"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#edf8fd] text-[#147db5] dark:bg-dark-soft-blue dark:text-dark-primary">
              <Users size={22} />
            </div>
            <h3 className="mt-4 text-lg font-bold">
              {error
                ? "We couldn’t check your team’s connection"
                : "Your team’s inbox isn’t available yet"}
            </h3>
            <p className="mt-2 max-w-xl text-sm leading-6 text-text-secondary dark:text-dark-text-secondary">
              {error
                ? "Refresh to check your organisation’s shared WhatsApp connection again."
                : accounts.length
                  ? "Your owner or admin needs to choose a default sending number in WhatsApp Settings. You’ll then use that number automatically."
                  : "Your WhatsApp access is active. The existing inbox still needs to be linked to this organisation, or connected by your owner or admin."}
            </p>
            <p className="mt-2 text-sm text-text-secondary dark:text-dark-text-secondary">
              There’s no separate setup or WhatsApp account needed for you.
            </p>
            <button
              type="button"
              onClick={reload}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#147db5] px-4 py-3 text-sm font-semibold text-white hover:bg-[#0f5b8a]"
            >
              <RefreshCw size={16} />
              Refresh connection
            </button>
          </section>
        )}

        {/* WhatsApp Connection */}
        {!memberWaiting && (
          <section>
            <div className="mb-3.5 flex items-end justify-between gap-4 px-1">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#8194a0] dark:text-dark-text-muted">
                  Connection path
                </p>

                <h2 className="mt-1 text-[14px] font-bold leading-5 text-[#173044] dark:text-dark-text-primary">
                  Account and sending number
                </h2>

                <p className="mt-1 max-w-2xl text-[10px] leading-4 text-[#8194a0] dark:text-dark-text-muted">
                  Pick an account first, then select the number that should
                  handle messaging.
                </p>
              </div>
              <span className="hidden rounded-full border border-[#d8e7ed] bg-white px-2.5 py-1 text-[8px] font-bold text-[#607787] sm:inline dark:border-dark-border dark:bg-dark-card dark:text-dark-text-secondary">
                {Number(Boolean(whatsappAccountId)) +
                  Number(Boolean(phoneNumberId))}{" "}
                selected resources
              </span>
            </div>

            {/* Account + Phone */}
            <div className="grid w-full grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-2">
              <div className="min-w-0">
                <WhatsAppAccountSection
                  accounts={accounts}
                  selectedAccountId={whatsappAccountId}
                  loading={loadingAccounts}
                  workspaceId={workspaceId}
                  onSelect={handleAccountChange}
                  onAdd={canManageConnection ? openAccountModal : undefined}
                  onEdit={
                    canManageConnection ? openEditAccountModal : undefined
                  }
                />
              </div>

              <div className="min-w-0">
                <PhoneNumberSection
                  phoneNumbers={phoneNumbers}
                  selectedPhoneId={phoneNumberId}
                  loading={loadingPhones}
                  workspaceId={workspaceId}
                  accountId={whatsappAccountId}
                  onSelect={handlePhoneNumberChange}
                  onAdd={canManageConnection ? openPhoneModal : undefined}
                />
              </div>
            </div>
          </section>
        )}

        {/* Active Connection */}
        {hasConnection && !memberWaiting && (
          <section>
            <ActiveConnection
              status={connectionStatus}
              workspaceName={workspaceName}
              accountName={accountName}
              phoneName={phoneName}
              phoneNumber={phoneNumber}
            />
          </section>
        )}

        {canSetDefaultConnection && hasConnection && (
          <section className="rounded-2xl border border-[#d8e7ed] bg-white p-5 dark:border-dark-border dark:bg-dark-card">
            {isDefaultConnection ? (
              <p className="flex items-center gap-2 text-sm font-semibold text-[#147db5] dark:text-dark-primary">
                <CircleCheckBig size={18} />
                This is your organisation’s default number
              </p>
            ) : (
              <label className="flex items-start gap-3 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={useAsDefault}
                  onChange={(event) => setUseAsDefault(event.target.checked)}
                  disabled={saving}
                  className="mt-1 accent-[#147db5]"
                />
                Use this number for new team members
              </label>
            )}
            <p className="mt-2 text-sm leading-6 text-text-secondary dark:text-dark-text-secondary">
              Teammates open this inbox automatically. People who already chose
              a number keep their selection.
            </p>
          </section>
        )}

        {/* Save */}
        {!memberWaiting && (
          <section className="pb-4">
            <SaveConnectionBar
              isSetupMode={isSetupMode}
              saved={saved}
              saving={saving}
              disabled={
                loading || loadingAccounts || loadingPhones || !hasConnection
              }
              onSave={handleSave}
            />
          </section>
        )}
      </div>

      {/* =====================================================
        MODALS
    ===================================================== */}

      {modal === "workspace" && (
        <WorkspaceModal
          form={workspaceForm}
          setForm={setWorkspaceForm}
          creating={creating}
          onClose={() => setModal(null)}
          onSubmit={handleCreateWorkspace}
        />
      )}

      {modal === "account" && (
        <WhatsAppAccountModal
          form={accountForm}
          setForm={setAccountForm}
          creating={creating}
          mode={accountModalMode}
          error={error}
          onClose={() => {
            setError("");
            setModal(null);
          }}
          onSubmit={
            accountModalMode === "edit"
              ? handleUpdateAccount
              : handleCreateAccount
          }
        />
      )}

      {modal === "phone" && (
        <PhoneNumberModal
          form={phoneForm}
          setForm={setPhoneForm}
          creating={creating}
          onClose={() => setModal(null)}
          onSubmit={handleCreatePhoneNumber}
        />
      )}
    </div>
  );
}

export default WhatsAppConnectionSettings;
